import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  BatteryDetailsType,
  BatteryListItemType,
  BatteryType,
  CreateBatteryDto,
  UpdateBatteryDto,
} from '../../types/battery/battery.type';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postBatteries = createAsyncThunk<
  AsyncTaskType,
  {
    data: {
      equipment_group: { id: number };
      owner?: { id: number };
      batteries: Array<{
        brand: string;
        description: string;
        model: string;
        serial: string;
        is_inventory: boolean;
      }>;
    };
  },
  { state: RootReducerState }
>('batteries/postBatteries', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/battery/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating batteries!' });
  }
});

export const postBatteryDetails = createAsyncThunk<
  Pick<BatteryType, 'brand' | 'description' | 'id' | 'is_inventory' | 'model' | 'serial'>,
  { data: CreateBatteryDto },
  { state: RootReducerState }
>('batteries/postBatteryDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<
      Pick<BatteryType, 'brand' | 'description' | 'id' | 'is_inventory' | 'model' | 'serial'>
    >(`${process.env.REACT_APP_API_URL}/battery`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating battery!' });
  }
});

export const getBatteriesList = createAsyncThunk<
  PaginationType<BatteryListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('batteries/getBatteriesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<BatteryListItemType>>(
      `${process.env.REACT_APP_API_URL}/battery${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching batteries!' });
  }
});

export const getBatteryDetails = createAsyncThunk<BatteryDetailsType, { id: number }, { state: RootReducerState }>(
  'batteries/getBatteryDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<BatteryDetailsType>(`${process.env.REACT_APP_API_URL}/battery/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching battery!' });
    }
  }
);

export const patchBatteryDetails = createAsyncThunk<
  BatteryType,
  { id: number; data: UpdateBatteryDto },
  { state: RootReducerState }
>('batteries/patchBatteryDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<BatteryType>(`${process.env.REACT_APP_API_URL}/battery/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating battery!' });
  }
});

export const deleteBatteryDetails = createAsyncThunk<
  Pick<
    BatteryType,
    | 'brand'
    | 'created_at'
    | 'description'
    | 'equipment_group'
    | 'hash'
    | 'is_inventory'
    | 'model'
    | 'modified_at'
    | 'serial'
  >,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('batteries/deleteBatteryDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<
      Pick<
        BatteryType,
        | 'brand'
        | 'created_at'
        | 'description'
        | 'equipment_group'
        | 'hash'
        | 'is_inventory'
        | 'model'
        | 'modified_at'
        | 'serial'
      >
    >(`${process.env.REACT_APP_API_URL}/battery/${id}`);
    if (fetchList) thunkAPI.dispatch(getBatteriesList(thunkAPI.getState().batteriesReducer.batteriesRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting battery!' });
  }
});

export const postBatteryOwnershipTransfer = createAsyncThunk<
  void,
  {
    id: number;
    data: {
      owner: { id: number };
    };
  },
  { state: RootReducerState; rejectValue: string }
>('batteries/postBatteryOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/battery/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Battery ownership transfer!' });
  }
});
